import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  OG_IMAGE_ALT,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE_LANGUAGE,
  SITE_LOCALE,
  SITE_NAME,
  SITE_URL,
  TWITTER_HANDLE,
  buildCanonicalUrl,
  formatDocumentTitle,
} from '../../config/seo';

function upsertMetaByName(name, content) {
  if (content == null || content === '') return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaByProperty(property, content) {
  if (content == null || content === '') return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return;
  const selector = [`link[rel="${rel}"]`];
  if (extra.hreflang) selector.push(`[hreflang="${extra.hreflang}"]`);
  let el = document.querySelector(selector.join(''));
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extra.hreflang) el.setAttribute('hreflang', extra.hreflang);
    if (extra.type) el.setAttribute('type', extra.type);
    if (extra.title) el.setAttribute('title', extra.title);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertHreflang(canonical) {
  upsertLink('alternate', canonical, { hreflang: SITE_LANGUAGE });
  upsertLink('alternate', canonical, { hreflang: 'x-default' });
}

function upsertSitemapLink() {
  upsertLink('sitemap', `${SITE_URL}/sitemap.xml`, {
    type: 'application/xml',
    title: 'Sitemap',
  });
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  const payload = Array.isArray(data)
    ? { '@context': 'https://schema.org', '@graph': data }
    : data;
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

/**
 * Apply document head SEO for the current SPA route.
 */
export function applyPageSeo({
  pathname = '/',
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = 'website',
  noindex,
  nofollow,
  jsonLd,
}) {
  const docTitle = formatDocumentTitle(title || DEFAULT_TITLE);
  const desc = description || DEFAULT_DESCRIPTION;
  const keys = keywords || DEFAULT_KEYWORDS;
  const canonical = buildCanonicalUrl(canonicalPath ?? pathname);
  const image = ogImage || DEFAULT_OG_IMAGE;

  document.title = docTitle;

  upsertMetaByName('description', desc);
  upsertMetaByName('keywords', keys);

  const googleVerification = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;
  if (googleVerification) {
    upsertMetaByName('google-site-verification', googleVerification);
  }

  const index = noindex ? 'noindex' : 'index';
  const follow = nofollow ? 'nofollow' : 'follow';
  upsertMetaByName('robots', `${index}, ${follow}`);

  upsertLink('canonical', canonical);
  if (!noindex) {
    upsertHreflang(canonical);
  }
  upsertSitemapLink();

  upsertMetaByProperty('og:site_name', SITE_NAME);
  upsertMetaByProperty('og:title', docTitle);
  upsertMetaByProperty('og:description', desc);
  upsertMetaByProperty('og:url', canonical);
  upsertMetaByProperty('og:image', image);
  upsertMetaByProperty('og:image:secure_url', image.startsWith('http:') ? image.replace(/^http:/, 'https:') : image);
  upsertMetaByProperty('og:image:width', String(OG_IMAGE_WIDTH));
  upsertMetaByProperty('og:image:height', String(OG_IMAGE_HEIGHT));
  upsertMetaByProperty('og:image:alt', OG_IMAGE_ALT);
  upsertMetaByProperty('og:type', ogType);
  upsertMetaByProperty('og:locale', SITE_LOCALE);

  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:title', docTitle);
  upsertMetaByName('twitter:description', desc);
  upsertMetaByName('twitter:image', image);
  upsertMetaByName('twitter:image:alt', OG_IMAGE_ALT);
  if (TWITTER_HANDLE) {
    upsertMetaByName('twitter:site', `@${TWITTER_HANDLE}`);
    upsertMetaByName('twitter:creator', `@${TWITTER_HANDLE}`);
  }

  upsertJsonLd('qnaread-page-json-ld', jsonLd || null);
}
